import { API_ENDPOINTS, getAuthHeaders } from '../config/api.config';
import { 
  UniversitiesResponse, 
  UniversityRankingResponse, 
  SupportUniversityPayload, 
  SupportUniversityResponse,
  SupportUniversityErrorResponse 
} from '../types/university.types';

/**
 * Service pour gérer les appels API liés aux universités
 */
export const universityService = {
  /**
   * Récupère les universités du lieu actif pour l'édition active
   * Si un token d'authentification est disponible, il sera inclus dans la requête
   * pour indiquer si l'utilisateur a voté pour chaque université
   * @returns Réponse de l'API avec la liste des universités
   */
  async getActiveVenueUniversities(): Promise<UniversitiesResponse> {
    try {
      // Utilisation de getAuthHeaders pour inclure le token d'authentification
      const headers = getAuthHeaders();
      const response = await fetch(API_ENDPOINTS.UNIVERSITIES.ACTIVE_VENUE, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des universités:', error);
      throw error;
    }
  },

  /**
   * Récupère le classement des universités du lieu actif pour l'édition active
   * Si un token d'authentification est disponible, il sera inclus dans la requête
   * pour indiquer si l'utilisateur a voté pour chaque université
   * @returns Réponse de l'API avec le classement des universités
   */
  async getUniversitiesRanking(): Promise<UniversityRankingResponse> {
    try {
      // Utilisation de getAuthHeaders pour inclure le token d'authentification
      const headers = getAuthHeaders();
      const response = await fetch(API_ENDPOINTS.UNIVERSITIES.RANKING, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération du classement des universités:', error);
      throw error;
    }
  },

  /**
   * Permet à un utilisateur authentifié de voter pour une université
   * @param payload Données du vote (university_id, edition_id, etc.)
   * @returns Promesse avec la réponse de l'API
   */
  async supportUniversity(payload: SupportUniversityPayload): Promise<SupportUniversityResponse> {
    try {
      // Vérification que l'utilisateur est authentifié
      const headers = getAuthHeaders();
      if (!headers['Authorization']) {
        throw new Error('Vous devez être connecté pour supporter une université');
      }

      const response = await fetch(API_ENDPOINTS.SUPPORTERS.SUPPORT, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      // Même si la réponse n'est pas OK, on retourne les données pour traiter les erreurs spécifiques
      return data;
    } catch (error) {
      console.error('Erreur lors du vote pour une université:', error);
      // Créer une réponse d'erreur formatée
      const errorResponse: SupportUniversityErrorResponse = {
        status: 'error',
        message: error instanceof Error ? error.message : 'Une erreur est survenue lors du vote'
      };
      throw errorResponse;
    }
  }
};
